import { HttpError } from '@littlebird/auth-client';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';

import {
  type Skill,
  useProvisionTeamSkillMutation,
  useRemoveTeamSkillMutation,
  useSetTeamSkillStateMutation,
  useShareSkillMutation,
} from './plugins-query';

const REMOVE_CONFIRMATION = {
  title: 'Remove from your team?',
  description: 'It stops showing up for your team.',
  confirmText: 'Remove',
  cancelText: 'Cancel',
};

export const useTeamSkillActions = () => {
  const share = useShareSkillMutation();
  const remove = useRemoveTeamSkillMutation();
  const provision = useProvisionTeamSkillMutation();
  const setState = useSetTeamSkillStateMutation();
  const isPending =
    share.isPending ||
    remove.isPending ||
    provision.isPending ||
    setState.isPending;

  const shareSkill = (skill: Skill) => {
    const isUpdate = Boolean(skill.teamShare);
    share.mutate(skill.name, {
      onSuccess: () =>
        toast.success(
          isUpdate ? 'Team version updated' : 'Shared with your team'
        ),
      onError: (error) =>
        toast.error(
          error instanceof HttpError
            ? error.message
            : 'Could not share this skill with your team'
        ),
    });
  };

  const removeFromTeam = (id: string) =>
    remove.mutate(id, {
      onSuccess: () => toast.success('Removed from your team'),
      onError: (error) =>
        toast.error(
          error instanceof HttpError
            ? error.message
            : 'Could not remove this skill from your team'
        ),
    });

  const confirmRemove = async (id: string) => {
    const confirmed = await ModalsManager.showResolvable(
      ConfirmDialog,
      REMOVE_CONFIRMATION
    );
    if (confirmed) removeFromTeam(id);
  };

  const setEnabled = (id: string, enabled: boolean) =>
    setState.mutate(
      { id, enabled },
      { onError: () => toast.error('Could not update this skill') }
    );

  const setProvisioned = (id: string, provisioned: boolean) =>
    provision.mutate(
      { id, provisioned },
      { onError: () => toast.error('Could not update this skill') }
    );

  return {
    isPending,
    shareSkill,
    removeFromTeam,
    confirmRemove,
    setEnabled,
    setProvisioned,
  };
};
